import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import UserMenu from "../../components/ui/UserMenu";
import MatrixBackground from "../../components/ui/MatrixBackground";
import { motion } from 'framer-motion';
import { scrollToSection } from '../../utils/scroll';
import SEO from '../../components/seo/SEO';
import StructuredData from '../../components/seo/StructuredData';
import { SITE_CONFIG } from '../../config/constants';

// Section Components
import Hero from '../../components/sections/Hero';
import WhyDataEngineering from '../../components/sections/WhyDataEngineering';
import AboutMe from '../../components/sections/AboutMe';
import Testimonials from '../../components/sections/Testimonials';

import Contact from '../../components/sections/Contact';
import Footer from '../../components/sections/Footer';

interface HomeProps {
  user?: any;
  onUpdateSubscription: (subscribed: boolean) => void;
  onLogout?: () => void;
}

export default function Home({ user, onUpdateSubscription, onLogout }: HomeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Manejar hash links y scrollto links al cargar la página
  useEffect(() => {
    const scrollToSectionFromStorage = sessionStorage.getItem('scrollToSection');
    if (scrollToSectionFromStorage) {
      sessionStorage.removeItem('scrollToSection');
      setTimeout(() => {
        scrollToSection(scrollToSectionFromStorage);
      }, 300);
      return;
    }
    
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    }
  }, [location.hash]);


  return (
    <>
      <SEO
        title={SITE_CONFIG.seo.title}
        description={SITE_CONFIG.seo.description}
        keywords={SITE_CONFIG.seo.keywords}
        url="/"
      />
      <StructuredData
        type="Person"
        data={{
          name: SITE_CONFIG.personal.fullName,
          jobTitle: SITE_CONFIG.personal.title,
          url: SITE_CONFIG.urls.website,
          sameAs: [
            SITE_CONFIG.social.linkedin,
            SITE_CONFIG.social.github,
            SITE_CONFIG.social.twitter,
          ],
        }}
      />
    <div className="min-h-screen bg-black font-sans relative">
      {/* Fondo Matrix */}
      <div className="fixed inset-0 z-0">
        <MatrixBackground color="#00ff41" animated={true} className="w-full h-full" />
      </div>
      <div className="fixed inset-0 z-[1] bg-black/75" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xl font-bold text-green-400"
            >
              <Link to="/" className="hover:text-green-300 transition-colors">Luciano Argolo</Link>
            </motion.div>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/mentorias" className="text-green-400 hover:text-green-300 transition-colors font-medium">
                Mentorías
              </Link>
              <Link to="/cursos" className="text-green-400 hover:text-green-300 transition-colors font-medium">
                Cursos
              </Link>
              <Link to="/bootcamps" className="text-green-400 hover:text-green-300 transition-colors font-medium">
                Bootcamps
              </Link>
              {user && (
                <Link to="/mi-linkedin" className="text-green-400 hover:text-green-300 transition-colors font-medium">
                  Mi Posicionamiento
                </Link>
              )}
              <button onClick={() => scrollToSection('sobre-mi')} className="text-green-400 hover:text-green-300 transition-colors">
                Sobre mí
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-green-400 hover:text-green-300 transition-colors">
                Contacto
              </button>
              {user && onLogout ? (
                <UserMenu user={user} onLogout={onLogout} />
              ) : (
                <Link to="/login" className="text-green-400 hover:text-green-300 transition-colors">
                  Iniciar Sesión
                </Link>
              )}
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-green-400 hover:text-green-300 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mt-4 pb-4 border-t border-green-500/30">
              <div className="flex flex-col space-y-3 pt-4">
                <Link to="/mentorias" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Mentorías</Link>
                <Link to="/cursos" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Cursos</Link>
                <Link to="/bootcamps" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Bootcamps</Link>
                {user && <Link to="/mi-linkedin" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Mi Posicionamiento</Link>}
                <button onClick={() => { scrollToSection('sobre-mi'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Sobre mí</button>
                <button onClick={() => { scrollToSection('contacto'); setMobileMenuOpen(false); }} className="text-green-400 hover:text-green-300 transition-colors text-left">Contacto</button>
                {user ? (
                  <>
                    <div className="border-t border-green-500/30 my-2"></div>
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Mi perfil</Link>
                    <Link to="/cursos" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Mis cursos</Link>
                    <button onClick={() => { setMobileMenuOpen(false); onLogout?.(); }} className="text-red-400 hover:text-red-300 transition-colors text-left">Cerrar sesión</button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-green-400 hover:text-green-300 transition-colors text-left">Iniciar Sesión</Link>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-24 md:pb-20 px-6 bg-black/70">
        <Hero
          onIniciaRedirect={() => navigate('/Inicia')}
          onScrollToSection={scrollToSection}
          darkBg
        />
      </section>

      {/* AboutMe — "Yo lo viví" (credibilidad) */}
      <section className="relative py-24 bg-black/80">
        <AboutMe />
      </section>

      {/* WhyDataEngineering — "Mirá los sueldos" (oportunidad) */}
      <section className="relative py-24 bg-black/75">
        <WhyDataEngineering />
      </section>

      {/* Testimonials — "Les funcionó" (prueba social antes del cierre) */}
      <section id="testimonios" className="relative py-6 bg-black/75 border-b border-green-500/20">
        <Testimonials compact darkBg />
      </section>

      {/* Contact + CTA final */}
      <section className="relative py-24 bg-black/80">
        <Contact />
      </section>

      {/* Footer */}
      <section className="relative">
        <Footer />
      </section>
      </div>
    </div>
    </>
  );
}
